import React, { useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { BaseComponentState, ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type ContainerElement =
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'main'
  | 'header'
  | 'footer'
  | 'nav'
  | 'figure'
  | 'figcaption'
  | 'address'
  | 'details'
  | 'summary'
  | 'dialog'
  | 'menu'
  | 'hgroup';

interface ContainerBoxVariant {
  padding?: ComponentSize | 'none';
  rounded?: ComponentSize | 'none' | 'full';
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  background?: 'none' | 'primary' | 'secondary' | 'surface' | 'muted';
}

interface ContainerBoxProps extends React.HTMLAttributes<HTMLElement>, ForwardedHTMLProps, BaseComponentState {
  children?: React.ReactNode;
  className?: string;
  as?: ContainerElement;
  variant?: ContainerBoxVariant;
  centered?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  overflow?: 'auto' | 'hidden' | 'scroll' | 'visible';
}

const paddingMap: Record<ComponentSize | 'none', string> = {
  none: '',
  xs: 'p-1',
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

const roundedMap: Record<ComponentSize | 'none' | 'full', string> = {
  none: 'rounded-none',
  xs: 'rounded-sm',
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-xl',
  xl: 'rounded-2xl',
  full: 'rounded-full',
};

const shadowMap: Record<'none' | 'sm' | 'md' | 'lg' | 'xl', string> = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
};

const backgroundMap: Record<'none' | 'primary' | 'secondary' | 'surface' | 'muted', string> = {
  none: '',
  primary: 'bg-primary text-white',
  secondary: 'bg-slate-100 dark:bg-slate-800',
  surface: 'bg-white dark:bg-slate-900',
  muted: 'bg-slate-50 dark:bg-slate-800/50',
};

const overflowMap: Record<'auto' | 'hidden' | 'scroll' | 'visible', string> = {
  auto: 'overflow-auto',
  hidden: 'overflow-hidden',
  scroll: 'overflow-scroll',
  visible: 'overflow-visible',
};

const DEFAULT_VARIANT: ContainerBoxVariant = {};

export const ContainerBox = React.memo(
  React.forwardRef<HTMLElement, ContainerBoxProps>(
    (
      {
        children,
        className,
        as = 'div',
        variant = DEFAULT_VARIANT,
        centered = false,
        fullWidth = false,
        fullHeight = false,
        overflow,
        loading = false,
        disabled = false,
        error = false,
        ...props
      },
      ref
    ) => {
      const forwardedProps = forwardProps(props);

      const Component = as as React.ElementType;

      const containerClasses = useMemo(() => {
        const { padding = 'none', rounded = 'none', shadow = 'none', border = false, background = 'none' } = variant;

        return cn(
          paddingMap[padding],
          roundedMap[rounded],
          shadowMap[shadow],
          backgroundMap[background],
          border && 'border border-slate-200 dark:border-slate-700',
          centered && 'flex items-center justify-center',
          fullWidth && 'w-full',
          fullHeight && 'h-full',
          overflow && overflowMap[overflow],
          loading && 'animate-pulse',
          disabled && 'opacity-50 pointer-events-none',
          error && 'border-red-500',
          className
        );
      }, [variant, centered, fullWidth, fullHeight, overflow, loading, disabled, error, className]);

      return (
        <Component
          ref={ref}
          className={containerClasses}
          aria-busy={loading || undefined}
          aria-disabled={disabled || undefined}
          {...forwardedProps}
          {...props}
        >
          {children}
        </Component>
      );
    }
  )
);

ContainerBox.displayName = 'ContainerBox';

export default ContainerBox;
